/* NMR PREMIUM SHOWCASE R72 — DESKTOP AUTOPLAY / TOUCH NATIVE */
(() => {
  'use strict';

  const ROOT = '[data-nmr-premium-showcase]';

  const DWELL_MS = 3000;
  const AUTO_MOVE_MS = 980;
  const MANUAL_MOVE_MS = 680;
  const DRAG_SNAP_MS = 420;

  const clamp = (value, min, max) =>
    Math.min(max, Math.max(min, value));

  const easeInOutQuint = (t) =>
    t < 0.5
      ? 16 * t * t * t * t * t
      : 1 - Math.pow(-2 * t + 2, 5) / 2;

  function bind(section) {
    if (!section || section.dataset.nmrPremiumBound === 'true') return;

    const viewport = section.querySelector('[data-nmr-premium-viewport]');
    const track = section.querySelector('[data-nmr-premium-track]');
    const prev = section.querySelector('[data-nmr-premium-prev]');
    const next = section.querySelector('[data-nmr-premium-next]');

    if (!viewport || !track) return;

    section.dataset.nmrPremiumBound = 'true';

    const isTouchDevice =
      (
        navigator.maxTouchPoints > 0
      ) ||
      window.matchMedia?.('(pointer: coarse)').matches;

    /*
      Touch devices are intentionally left native:
      - no autoplay
      - no JS pointer drag
      - no JS snap
      Browser/Safari owns the gesture completely.
    */
    if (isTouchDevice) {
      section.dataset.nmrPremiumMode = 'touch-native';

      viewport.style.scrollBehavior = 'auto';
      viewport.style.scrollSnapType = '';

      bindAddToCart(section);

      return;
    }

    section.dataset.nmrPremiumMode = 'desktop-autoplay';

    let autoTimer = 0;
    let animationFrame = 0;
    let resizeFrame = 0;

    let direction = 1;
    let logicalIndex = 0;

    let dragging = false;
    let dragActivated = false;
    let dragStartIndex = 0;
    let dragStartX = 0;
    let dragStartScroll = 0;
    let suppressClickUntil = 0;

    function cards() {
      return Array.from(
        track.querySelectorAll('[data-nmr-premium-card]')
      );
    }

    function step() {
      const items = cards();

      if (!items.length) {
        return Math.max(1, viewport.clientWidth * 0.8);
      }

      if (items.length > 1) {
        const distance =
          items[1].offsetLeft -
          items[0].offsetLeft;

        if (
          Number.isFinite(distance) &&
          Math.abs(distance) > 1
        ) {
          return Math.abs(distance);
        }
      }

      const rect = items[0].getBoundingClientRect();
      const styles = getComputedStyle(track);

      const gap =
        Number.parseFloat(
          styles.columnGap ||
          styles.gap ||
          '0'
        ) || 0;

      return Math.max(1, rect.width + gap);
    }

    function maxScroll() {
      return Math.max(
        0,
        viewport.scrollWidth -
        viewport.clientWidth
      );
    }

    function maxIndex() {
      const unit = step();

      if (unit <= 0) return 0;

      return Math.max(
        0,
        Math.round(maxScroll() / unit)
      );
    }

    function nearestIndex() {
      const unit = step();

      if (unit <= 0) return 0;

      return clamp(
        Math.round(viewport.scrollLeft / unit),
        0,
        maxIndex()
      );
    }

    function positionForIndex(index) {
      return clamp(
        index * step(),
        0,
        maxScroll()
      );
    }

    function setIndex(index) {
      logicalIndex =
        clamp(index, 0, maxIndex());

      section.dataset.nmrPremiumIndex =
        String(logicalIndex);
    }

    function setState(state) {
      section.dataset.nmrPremiumMotion = state;
    }

    function clearAutoTimer() {
      if (!autoTimer) return;

      clearTimeout(autoTimer);
      autoTimer = 0;
    }

    function cancelAnimation() {
      if (!animationFrame) return;

      cancelAnimationFrame(animationFrame);
      animationFrame = 0;
    }

    function cancelMotion() {
      clearAutoTimer();
      cancelAnimation();
    }

    function animateTo(target, duration, onDone) {
      cancelAnimation();

      const start = viewport.scrollLeft;
      const end = clamp(target, 0, maxScroll());
      const delta = end - start;

      if (
        Math.abs(delta) < 0.5 ||
        duration <= 0
      ) {
        viewport.scrollLeft = end;
        setIndex(nearestIndex());

        if (typeof onDone === 'function') {
          onDone();
        }

        return;
      }

      const previousSnap =
        viewport.style.scrollSnapType;

      viewport.style.scrollSnapType = 'none';
      viewport.style.scrollBehavior = 'auto';

      const startedAt = performance.now();

      setState('moving');

      function frame(now) {
        const progress = clamp(
          (now - startedAt) / duration,
          0,
          1
        );

        const eased =
          easeInOutQuint(progress);

        viewport.scrollLeft =
          start + (delta * eased);

        if (progress < 1) {
          animationFrame =
            requestAnimationFrame(frame);
          return;
        }

        animationFrame = 0;
        viewport.scrollLeft = end;

        viewport.style.scrollSnapType =
          previousSnap;

        setIndex(nearestIndex());

        if (typeof onDone === 'function') {
          onDone();
        }
      }

      animationFrame =
        requestAnimationFrame(frame);
    }

    function scheduleAuto(delay = DWELL_MS) {
      clearAutoTimer();

      if (
        dragging ||
        document.hidden ||
        cards().length < 2
      ) {
        setState('paused');
        return;
      }

      setState('dwell');

      autoTimer =
        window.setTimeout(
          runAutoStep,
          delay
        );
    }

    function runAutoStep() {
      if (
        dragging ||
        document.hidden
      ) {
        scheduleAuto(DWELL_MS);
        return;
      }

      setIndex(nearestIndex());

      const last = maxIndex();

      if (last <= 0) {
        setState('idle');
        return;
      }

      if (
        direction > 0 &&
        logicalIndex >= last
      ) {
        direction = -1;
      } else if (
        direction < 0 &&
        logicalIndex <= 0
      ) {
        direction = 1;
      }

      let targetIndex =
        clamp(
          logicalIndex + direction,
          0,
          last
        );

      if (targetIndex === logicalIndex) {
        direction *= -1;

        targetIndex =
          clamp(
            logicalIndex + direction,
            0,
            last
          );
      }

      animateTo(
        positionForIndex(targetIndex),
        AUTO_MOVE_MS,
        () => {
          setIndex(targetIndex);
          scheduleAuto(DWELL_MS);
        }
      );
    }

    function manualMove(delta) {
      cancelMotion();

      setIndex(nearestIndex());

      const targetIndex =
        clamp(
          logicalIndex + delta,
          0,
          maxIndex()
        );

      if (targetIndex === logicalIndex) {
        scheduleAuto(DWELL_MS);
        return;
      }

      direction =
        delta >= 0 ? 1 : -1;

      animateTo(
        positionForIndex(targetIndex),
        MANUAL_MOVE_MS,
        () => {
          setIndex(targetIndex);
          scheduleAuto(DWELL_MS);
        }
      );
    }

    prev?.addEventListener(
      'click',
      () => manualMove(-1)
    );

    next?.addEventListener(
      'click',
      () => manualMove(1)
    );

    /*
      Desktop mouse drag only.
      Touch pointers never enter this branch because
      touch devices return before handlers are bound.
    */
    viewport.addEventListener(
      'pointerdown',
      (event) => {
        if (
          event.pointerType !== 'mouse' ||
          event.button !== 0
        ) {
          return;
        }

        cancelMotion();

        dragging = true;
        dragActivated = false;
        dragStartIndex = nearestIndex();
        dragStartX = event.clientX;
        dragStartScroll = viewport.scrollLeft;

        viewport.style.scrollBehavior = 'auto';
        viewport.style.scrollSnapType = 'none';

        section.dataset.nmrPremiumDrag = 'armed';

        viewport.setPointerCapture?.(
          event.pointerId
        );
      }
    );

    viewport.addEventListener(
      'pointermove',
      (event) => {
        if (
          !dragging ||
          event.pointerType !== 'mouse'
        ) {
          return;
        }

        const dx =
          event.clientX -
          dragStartX;

        if (
          !dragActivated &&
          Math.abs(dx) > 5
        ) {
          dragActivated = true;
          section.dataset.nmrPremiumDrag = 'active';
        }

        if (!dragActivated) return;

        event.preventDefault();

        viewport.scrollLeft =
          dragStartScroll - dx;
      }
    );

    function finishMouseDrag(event) {
      if (
        !dragging ||
        event.pointerType !== 'mouse'
      ) {
        return;
      }

      dragging = false;

      viewport.releasePointerCapture?.(
        event.pointerId
      );

      delete section.dataset.nmrPremiumDrag;

      const dx =
        event.clientX -
        dragStartX;

      let targetIndex =
        nearestIndex();

      if (
        dragActivated &&
        Math.abs(dx) >
          Math.max(40, step() * 0.12)
      ) {
        targetIndex =
          clamp(
            dragStartIndex +
            (dx < 0 ? 1 : -1),
            0,
            maxIndex()
          );
      }

      if (dragActivated) {
        suppressClickUntil =
          performance.now() + 260;
      }

      dragActivated = false;

      animateTo(
        positionForIndex(targetIndex),
        DRAG_SNAP_MS,
        () => {
          setIndex(targetIndex);
          scheduleAuto(DWELL_MS);
        }
      );
    }

    viewport.addEventListener(
      'pointerup',
      finishMouseDrag
    );

    viewport.addEventListener(
      'pointercancel',
      finishMouseDrag
    );

    viewport.addEventListener(
      'click',
      (event) => {
        if (
          performance.now() <
          suppressClickUntil
        ) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      true
    );

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          cancelMotion();
          setState('hidden');
          return;
        }

        setIndex(nearestIndex());
        scheduleAuto(DWELL_MS);
      }
    );

    window.addEventListener(
      'resize',
      () => {
        cancelAnimationFrame(resizeFrame);

        resizeFrame =
          requestAnimationFrame(() => {
            cancelMotion();

            setIndex(
              clamp(
                logicalIndex,
                0,
                maxIndex()
              )
            );

            viewport.scrollLeft =
              positionForIndex(logicalIndex);

            scheduleAuto(DWELL_MS);
          });
      },
      {passive:true}
    );

    bindAddToCart(section);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIndex(nearestIndex());
        scheduleAuto(DWELL_MS);
      });
    });
  }

  function bindAddToCart(section) {
    section
      .querySelectorAll('[data-nmr-premium-add]')
      .forEach((form) => {
        if (
          form.dataset.nmrPremiumAddBound === 'true'
        ) {
          return;
        }

        form.dataset.nmrPremiumAddBound = 'true';

        form.addEventListener(
          'submit',
          async (event) => {
            event.preventDefault();

            const button =
              form.querySelector(
                'button[type="submit"]'
              );

            const label =
              form.querySelector(
                '[data-nmr-premium-add-label]'
              );

            if (
              !button ||
              !label ||
              button.disabled
            ) {
              return;
            }

            const original =
              form.dataset.labelDefault ||
              label.textContent ||
              '';

            const added =
              form.dataset.labelAdded ||
              original;

            const error =
              form.dataset.labelError ||
              original;

            button.disabled = true;
            button.setAttribute(
              'aria-busy',
              'true'
            );

            try {
              const response =
                await fetch(
                  form.action,
                  {
                    method:'POST',
                    headers:{
                      'Accept':'application/json'
                    },
                    body:new FormData(form)
                  }
                );

              if (!response.ok) {
                throw new Error('add-failed');
              }

              const item =
                await response.json();

              label.textContent = added;

              document.dispatchEvent(
                new CustomEvent(
                  'nmr:premium-cart-added',
                  {
                    detail:{item},
                    bubbles:true
                  }
                )
              );

              window.setTimeout(() => {
                label.textContent =
                  original;
              }, 1400);
            } catch (_) {
              label.textContent = error;

              window.setTimeout(() => {
                label.textContent =
                  original;
              }, 1600);
            } finally {
              button.disabled = false;
              button.removeAttribute(
                'aria-busy'
              );
            }
          }
        );
      });
  }

  function init(root = document) {
    if (root.matches?.(ROOT)) {
      bind(root);
    }

    root
      .querySelectorAll?.(ROOT)
      .forEach(bind);
  }

  init();

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      init(event.target);
    }
  );
})();
